import { useCallback, useEffect, useRef, useState } from "react";
import { api, fileUrl, formatApiErrorDetail, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Loader2, Trash2, Upload, Download, AlertTriangle } from "lucide-react";

const prettySize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(Math.round(bytes / 1024), 1)} KB`;
};

export default function DocumentVault({ entityType = "property", entityId, canEdit = false }) {
  const [docs, setDocs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", category: "lease", expires_at: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/documents", { params: { entity_type: entityType, entity_id: entityId } })
      .then(({ data }) => setDocs(data))
      .catch(() => setDocs([]));
  }, [entityType, entityId]);

  useEffect(() => {
    api.get("/documents/meta").then(({ data }) => setMeta(data)).catch(() => {});
    load();
  }, [load]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadFile(file);
      await api.post("/documents", {
        entity_type: entityType,
        entity_id: entityId,
        name: form.name.trim() || file.name,
        category: form.category,
        storage_path: path,
        original_filename: file.name,
        content_type: file.type,
        size: file.size,
        expires_at: form.expires_at || null,
      });
      toast.success("Document saved to the vault");
      setForm({ name: "", category: form.category, expires_at: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save that document");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (d) => {
    try {
      await api.delete(`/documents/${d._id}`);
      toast.success(`${d.name} removed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove document");
    }
  };

  const categoryLabel = (value) =>
    (meta?.categories || []).find((cat) => cat.value === value)?.label || value;

  return (
    <div className="space-y-4" data-testid="document-vault">
      {canEdit && (
        <div className="rounded-lg border border-dashed border-royal/25 bg-white p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Document name</Label>
              <Input data-testid="document-name-input" placeholder="Optional — defaults to the file name"
                value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="document-category-select" className="mt-1.5 h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(meta?.categories || []).map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} data-testid={`doc-cat-${cat.value}`}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Expires (optional)</Label>
              <Input data-testid="document-expiry-input" type="date" value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                className="mt-1.5 h-11" />
            </div>
          </div>
          <input ref={fileRef} type="file" data-testid="document-file-input" onChange={pick}
            className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.txt,.csv" />
          <Button data-testid="document-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
            className="mt-4 h-11 gap-2 bg-royal hover:bg-royal-deep text-white font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload document
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            PDFs, photos and office files up to 15 MB. Your team can pull these up on site from any phone.
          </p>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="rounded-lg border border-border bg-white p-8 text-center" data-testid="documents-empty">
          <FileText className="h-6 w-6 text-gold mx-auto" />
          <p className="font-display text-base font-semibold text-royal-deep mt-3">Nothing filed yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Keep leases, warranties, permits and manuals here so nobody hunts through email for them.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white divide-y divide-border" data-testid="documents-list">
          {docs.map((d) => (
            <div key={d._id} className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5"
                 data-testid={`document-${d._id}`}>
              <FileText className="h-4 w-4 text-royal shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-royal-deep truncate">{d.name}</p>
                <p className="text-xs text-muted-foreground">
                  {categoryLabel(d.category)}
                  {d.size ? ` · ${prettySize(d.size)}` : ""}
                  {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""}
                  {d.expires_at ? ` · expires ${d.expires_at}` : ""}
                </p>
              </div>
              {d.expired && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700"
                      data-testid={`document-expired-${d._id}`}>
                  <AlertTriangle className="h-3 w-3" /> Expired
                </span>
              )}
              <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer"
                 data-testid={`document-open-${d._id}`}
                 className="inline-flex items-center gap-1.5 text-xs font-semibold text-royal hover:text-gold transition-colors duration-200">
                <Download className="h-3.5 w-3.5" /> Open
              </a>
              {canEdit && (
                <Button data-testid={`document-delete-${d._id}`} variant="outline" onClick={() => remove(d)}
                  className="h-9 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/5">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
